import { batch } from "solid-js"
import { produce, type SetStoreFunction, type Store } from "solid-js/store"
import type { ServerConnection } from "@/runtime/server/registry"
import { pathKey } from "@/workspaces/path-key"
import { renamedDirectory } from "@/workspaces/rename-path"
import { sameDirectory } from "@/workspaces/paths"
import {
  sidebarKey,
  type SidebarProjectListSort,
  type SidebarProjectRef,
  type SidebarRef,
  type SidebarSort,
  type SidebarState,
} from "./schema"

export { sidebarKey } from "./schema"
export type { SidebarProjectListSort, SidebarProjectRef, SidebarRef, SidebarSessionRef, SidebarSort } from "./schema"

export type SidebarProjectSnapshot = {
  ref: SidebarProjectRef
  pin?: { ref: SidebarRef; index: number }
  listIndex: number
  serverIndex: number
  expanded?: boolean
  sessionOrder?: string[]
  sessionSort?: SidebarSort
}

// Only the visible subset moves. Hidden projects and index-trimmed sessions keep their slots.
export function mergeSidebarOrder(saved: readonly string[], reordered: readonly string[]) {
  const next = [...new Set(reordered)]
  const included = new Set(next)
  let index = 0
  return [...new Set(saved)].map((key) => (included.has(key) ? next[index++] : key)).concat(next.slice(index))
}

export function orderSidebarItems<T>(items: readonly T[], order: readonly string[], key: (item: T) => string) {
  const positions = new Map(order.map((id, index) => [id, index]))
  return items.toSorted((a, b) => (positions.get(key(a)) ?? Infinity) - (positions.get(key(b)) ?? Infinity))
}

export function sortSidebarProjects<T>(
  items: readonly T[],
  options: {
    key: (item: T) => string
    name: (item: T) => string
    server: (item: T) => ServerConnection.Key
    updated: (item: T) => number
    order: readonly string[]
    legacy: (server: ServerConnection.Key) => readonly string[]
  },
) {
  const recent = items.toSorted(
    (a, b) =>
      options.updated(b) - options.updated(a) ||
      options.name(a).localeCompare(options.name(b)) ||
      options.key(a).localeCompare(options.key(b)),
  )
  if (options.order.length) return orderSidebarItems(recent, options.order, options.key)
  const groups = new Map(
    [...new Set(recent.map(options.server))].map((server) => [
      server,
      orderSidebarItems(
        recent.filter((item) => options.server(item) === server),
        options.legacy(server),
        options.key,
      ).values(),
    ]),
  )
  return recent.map((item) => groups.get(options.server(item))!.next().value!)
}

export function sortSidebarPins(
  pins: readonly SidebarRef[],
  mode: SidebarSort,
  metadata: (ref: SidebarRef) => { updated?: number; attention?: number },
) {
  if (mode === "manual") return [...pins]
  const sessions = pins
    .filter((ref) => ref.type === "session")
    .toSorted((a, b) => {
      const first = metadata(a)
      const second = metadata(b)
      if (mode === "attention") {
        const attention = (second.attention ?? 0) - (first.attention ?? 0)
        if (attention) return attention
      }
      return (second.updated ?? 0) - (first.updated ?? 0)
    })
  let index = 0
  return pins.map((ref) => (ref.type === "project" ? ref : sessions[index++]))
}

export function createSidebarState(state: Store<SidebarState>, setState: SetStoreFunction<SidebarState>) {
  const projectListSort = (): SidebarProjectListSort =>
    state.projectListSort ??
    (state.projectListOrder.length || Object.values(state.projectOrder).some((order) => order.length)
      ? "manual"
      : "updated")
  const isPinned = (ref: SidebarRef) => state.pins.some((pin) => sidebarKey(pin) === sidebarKey(ref))
  const remember = (ref: SidebarRef) => {
    const index = state.pins.findIndex((pin) => sidebarKey(pin) === sidebarKey(ref))
    if (index === -1) return
    const current = state.pins[index]
    const next = { ...current, ...ref }
    if (JSON.stringify(current) === JSON.stringify(next)) return
    setState("pins", index, next)
  }
  const unpin = (ref: SidebarRef) => {
    if (!isPinned(ref)) return
    setState("pins", (pins) => pins.filter((pin) => sidebarKey(pin) !== sidebarKey(ref)))
  }
  return {
    state,
    isPinned,
    remember,
    pin(ref: SidebarRef) {
      if (isPinned(ref)) {
        remember(ref)
        return
      }
      batch(() => {
        setState("pins", (pins) => [...pins, ref])
        setState("pinnedExpanded", true)
      })
    },
    unpin,
    reorderPinned(keys: readonly string[]) {
      const pins = new Map(state.pins.map((pin) => [sidebarKey(pin), pin]))
      const order = mergeSidebarOrder(
        [...pins.keys()],
        keys.filter((key) => pins.has(key)),
      )
      batch(() => {
        setState(
          "pins",
          order.map((key) => pins.get(key)!),
        )
        setState("pinnedSort", "manual")
      })
    },
    setPinnedSort(mode: SidebarSort) {
      setState("pinnedSort", mode)
    },
    setPinnedExpanded(expanded: boolean) {
      setState("pinnedExpanded", expanded)
    },
    setProjectsExpanded(expanded: boolean) {
      setState("projectsExpanded", expanded)
    },
    projectExpanded(ref: SidebarProjectRef) {
      return state.projectExpanded[sidebarKey(ref)]
    },
    setProjectExpanded(ref: SidebarProjectRef, expanded: boolean) {
      setState("projectExpanded", sidebarKey(ref), expanded)
    },
    projectOrder(server: ServerConnection.Key) {
      return state.projectOrder[server] ?? []
    },
    reorderProjects(server: ServerConnection.Key, keys: readonly string[]) {
      batch(() => {
        setState("projectOrder", server, mergeSidebarOrder(state.projectOrder[server] ?? [], keys))
        setState("projectListSort", "manual")
      })
    },
    projectListSort,
    setProjectListSort(mode: SidebarProjectListSort, keys: readonly string[] = []) {
      setState(
        produce((value) => {
          value.projectListSort = mode
          if (mode === "updated") {
            value.projectListOrder = []
            value.projectOrder = {}
            return
          }
          value.projectListOrder = mergeSidebarOrder(value.projectListOrder, keys)
        }),
      )
    },
    projectListOrder() {
      return state.projectListOrder
    },
    reorderProjectList(keys: readonly string[]) {
      batch(() => {
        setState("projectListOrder", mergeSidebarOrder(state.projectListOrder, keys))
        setState("projectListSort", "manual")
      })
    },
    sessionOrder(ref: SidebarProjectRef) {
      return state.sessionOrder[sidebarKey(ref)] ?? []
    },
    projectSessionSort(ref: SidebarProjectRef) {
      return state.sessionSort[sidebarKey(ref)] ?? "updated"
    },
    setProjectSessionSort(ref: SidebarProjectRef, mode: SidebarSort) {
      setState("sessionSort", sidebarKey(ref), mode)
    },
    reorderSessions(ref: SidebarProjectRef, sessionIDs: readonly string[]) {
      const key = sidebarKey(ref)
      batch(() => {
        setState("sessionOrder", key, mergeSidebarOrder(state.sessionOrder[key] ?? [], sessionIDs))
        setState("sessionSort", key, "manual")
      })
    },
    removeSessions(server: ServerConnection.Key, sessionIDs: readonly string[]) {
      const removed = new Set(sessionIDs)
      setState(
        produce((value) => {
          value.pins = value.pins.filter(
            (pin) => pin.type !== "session" || pin.server !== server || !removed.has(pin.sessionID),
          )
          for (const key of Object.keys(value.sessionOrder)) {
            if (!key.startsWith(JSON.stringify(["project", server]).slice(0, -1) + ",")) continue
            value.sessionOrder[key] = value.sessionOrder[key].filter((id) => !removed.has(id))
          }
        }),
      )
    },
    captureProject(ref: SidebarProjectRef): SidebarProjectSnapshot {
      const key = sidebarKey(ref)
      const index = state.pins.findIndex((pin) => sidebarKey(pin) === key)
      return {
        ref: { ...ref },
        pin: index === -1 ? undefined : { ref: { ...state.pins[index] }, index },
        listIndex: state.projectListOrder.indexOf(key),
        serverIndex: state.projectOrder[ref.server]?.indexOf(key) ?? -1,
        expanded: state.projectExpanded[key],
        sessionOrder: state.sessionOrder[key] ? [...state.sessionOrder[key]] : undefined,
        sessionSort: state.sessionSort[key],
      }
    },
    restoreProject(snapshot: SidebarProjectSnapshot) {
      const key = sidebarKey(snapshot.ref)
      const manual = projectListSort() === "manual"
      setState(
        produce((value) => {
          if (snapshot.pin && !value.pins.some((pin) => sidebarKey(pin) === key)) {
            value.pins.splice(snapshot.pin.index, 0, { ...snapshot.pin.ref })
          }
          if (manual && snapshot.listIndex !== -1 && !value.projectListOrder.includes(key)) {
            value.projectListOrder.splice(snapshot.listIndex, 0, key)
          }
          if (manual && snapshot.serverIndex !== -1) {
            const order = value.projectOrder[snapshot.ref.server] ?? []
            if (!order.includes(key)) order.splice(snapshot.serverIndex, 0, key)
            value.projectOrder[snapshot.ref.server] = order
          }
          if (snapshot.expanded !== undefined && value.projectExpanded[key] === undefined) {
            value.projectExpanded[key] = snapshot.expanded
          }
          if (snapshot.sessionOrder !== undefined && value.sessionOrder[key] === undefined) {
            value.sessionOrder[key] = [...snapshot.sessionOrder]
          }
          if (snapshot.sessionSort !== undefined && value.sessionSort[key] === undefined) {
            value.sessionSort[key] = snapshot.sessionSort
          }
        }),
      )
    },
    renameProject(server: ServerConnection.Key, previous: string, directory: string, name: string) {
      const source = sidebarKey({ type: "project", server, directory: previous }).slice(0, -2)
      const target = sidebarKey({ type: "project", server, directory }).slice(0, -2)
      const prefix = JSON.stringify(["project", server]).slice(0, -1) + ","
      const windows = /^[a-z]:\//i.test(pathKey(previous)) || pathKey(previous).startsWith("//")
      const renameKey = (key: string) => {
        if (!key.startsWith(prefix)) return key
        const current = windows ? key.toLowerCase() : key
        const expected = windows ? source.toLowerCase() : source
        return current === `${expected}\"]` || current.startsWith(`${expected}/`)
          ? `${target}${key.slice(source.length)}`
          : key
      }
      setState(
        produce((value) => {
          value.pins = value.pins.map((pin) => {
            if (pin.server !== server || !pin.directory) return pin
            const next = renamedDirectory(pin.directory, previous, directory)
            if (next === pin.directory) return pin
            return {
              ...pin,
              directory: next,
              ...(pin.type === "project" && sameDirectory(pin.directory, previous) ? { title: name } : {}),
            }
          })
          value.projectListOrder = value.projectListOrder.map(renameKey)
          if (value.projectOrder[server]) value.projectOrder[server] = value.projectOrder[server].map(renameKey)
          for (const record of [value.projectExpanded, value.sessionOrder, value.sessionSort]) {
            for (const key of Object.keys(record)) {
              const next = renameKey(key)
              if (next === key) continue
              Object.assign(record, { [next]: record[key] })
              delete record[key]
            }
          }
        }),
      )
    },
    removeProject(ref: SidebarProjectRef) {
      const key = sidebarKey(ref)
      const mode = projectListSort()
      if (
        !isPinned(ref) &&
        !state.projectListOrder.includes(key) &&
        !state.projectOrder[ref.server]?.includes(key) &&
        state.projectExpanded[key] === undefined &&
        state.sessionOrder[key] === undefined &&
        state.sessionSort[key] === undefined
      )
        return
      setState(
        produce((value) => {
          value.projectListSort = mode
          value.pins = value.pins.filter((pin) => sidebarKey(pin) !== key)
          value.projectListOrder = value.projectListOrder.filter((item) => item !== key)
          if (value.projectOrder[ref.server])
            value.projectOrder[ref.server] = value.projectOrder[ref.server].filter((item) => item !== key)
          delete value.projectExpanded[key]
          delete value.sessionOrder[key]
          delete value.sessionSort[key]
        }),
      )
    },
    removeServer(server: ServerConnection.Key) {
      const mode = projectListSort()
      setState(
        produce((value) => {
          value.projectListSort = mode
          value.pins = value.pins.filter((pin) => pin.server !== server)
          delete value.projectOrder[server]
          const prefix = JSON.stringify(["project", server]).slice(0, -1) + ","
          value.projectListOrder = value.projectListOrder.filter((key) => !key.startsWith(prefix))
          for (const record of [value.projectExpanded, value.sessionOrder, value.sessionSort]) {
            for (const key of Object.keys(record)) {
              if (key.startsWith(prefix)) delete record[key]
            }
          }
        }),
      )
    },
  }
}
